import { useState } from 'react'
import { mockQuotes } from '#app/data/mockQuotes'
import { Button } from '../../ui/button'
import { Icon } from '../../ui/icon'
import { InfoTooltip } from '../../ui/info-tooltip'
import { InsuranceQuoteCard } from '../../ui/insurance-quote-card'
import { Select, type SelectOption } from '../../ui/select'
import { Tag } from '../../ui/tag'

interface PlanSelectionScreenProps {
	onNext: (data: Record<string, any>) => void
	onBack: () => void
	data: Record<string, any>
}

export function PlanSelectionScreen({
	onNext,
	onBack,
	data,
}: PlanSelectionScreenProps) {
	const [selectedPlan] = useState(data.selectedPlan || 'ceibo')
	const [selectedQuote, setSelectedQuote] = useState<string | null>(null)
	const [compareList, setCompareList] = useState<string[]>([])
	const [sortBy] = useState('best-value')

	// Usar mock data centralizado
	const quotes = mockQuotes

	const sortOptions: SelectOption[] = [
		{ value: 'best-value', label: 'Mejor valor' },
		{ value: 'lowest-price', label: 'Menor precio' },
		{ value: 'highest-price', label: 'Mayor precio' },
	]

	const handleToggleCompare = (quoteId: string) => {
		setCompareList((prev) =>
			prev.includes(quoteId)
				? prev.filter((id) => id !== quoteId)
				: [...prev, quoteId],
		)
	}

	const handleSelectQuote = (quoteId: string) => {
		setSelectedQuote(quoteId)
		// Find the selected quote to pass complete data
		const selectedQuoteData = quotes.find((q) => q.id === quoteId)
		// Navigate directly to final screen (step 7), skipping comparison
		onNext({
			selectedQuote: quoteId,
			selectedQuoteData,
			skipToStep: 7,
			directSelection: true,
		})
	}

	const handleCompare = () => {
		if (compareList.length >= 2 && compareList.length <= 3) {
			// Pasar las cotizaciones completas seleccionadas para comparar
			const selectedQuotes = quotes.filter((q) => compareList.includes(q.id))
			onNext({ selectedQuote, compareList, quotesToCompare: selectedQuotes })
		}
	}

	const getPlanName = (planId: string) => {
		const plans: Record<string, string> = {
			ceibo: 'Plan Ceibo',
			jacaranda: 'Plan Jacarandá',
			roble: 'Plan Roble',
			algarrobo: 'Plan Algarrobo',
		}
		return plans[planId] || 'Plan'
	}

	const getPlanBadge = (planId: string) => {
		const badges: Record<string, string> = {
			ceibo: 'Responsabilidad civil',
			jacaranda: 'Terceros',
			roble: 'Terceros full',
			algarrobo: 'Franquicia',
		}
		return badges[planId] || ''
	}

	return (
		<div className="flex w-full flex-col gap-6 pb-20 md:gap-8 md:pb-0">
			{/* Header */}
			<div className="flex w-full flex-col items-start justify-between gap-3 md:flex-row md:items-start md:gap-0">
				<div className="flex flex-col gap-2">
					<h2 className="font-parkinsans text-[20px] leading-7 font-medium text-blue-800 md:text-[24px] md:leading-8">
						Elige tu seguro
					</h2>
					<p className="font-parkinsans text-sm leading-6 font-normal text-neutral-900 md:text-base md:leading-[30px]">
						Compara y elige tu cobertura ideal.
					</p>
				</div>
				<InfoTooltip
					title="¿Qué es el sello verde?"
					content={
						<>
							<p className="mb-1">
								Nuestros planes suman acciones de impacto ambiental:
							</p>
							<ul className="ml-[18px] list-disc space-y-0">
								<li>Compensación de huella de carbono.</li>
								<li>Apoyo a programas de educación ambiental.</li>
								<li>Aporte a energías limpias.</li>
							</ul>
						</>
					}
				>
					<Tag
						variant="secondary"
						appearance="solid"
						color="green"
						size="l"
						corner="rounded"
						option="custom"
						className="h-auto gap-2 rounded-lg px-2 py-2"
					>
						<div className="flex items-center gap-1">
							<Icon
								name="natural-food"
								className="size-[18px] text-green-500"
							/>
							<p className="font-parkinsans text-base leading-5 font-medium text-green-500">
								Sello Verde
							</p>
						</div>
						<Icon
							name="question-mark-circled"
							className="size-4 text-green-600"
						/>
					</Tag>
				</InfoTooltip>
			</div>

			{/* Plan selector and sort */}
			<div className="flex w-full flex-col items-start justify-between gap-4 md:flex-row md:items-center">
				<div className="flex w-full flex-wrap items-center gap-3">
					<div className="flex items-center gap-2">
						<Icon name="natural-food" className="size-5 text-green-500" />
						<p className="font-parkinsans text-base leading-5 font-medium text-blue-800">
							{getPlanName(selectedPlan)}
						</p>
						<p className="font-parkinsans text-sm leading-5 font-normal text-neutral-700">
							{getPlanBadge(selectedPlan)}
						</p>
					</div>
					<Button
						variant="primary"
						appearance="stroke"
						size="m"
						className="rounded-lg px-4 py-2"
						onClick={onBack}
					>
						Cambiar plan
					</Button>
				</div>
				<div className="flex w-full items-center gap-2 md:w-auto">
					<p className="font-parkinsans text-sm leading-5 font-normal text-neutral-700">
						Ordenar por
					</p>
					<Select
						options={sortOptions}
						value={sortBy}
						size="m"
						className="flex-1 md:w-[180px]"
					/>
				</div>
			</div>

			{/* Insurance quotes grid - single column on mobile, 2 on tablet, 4 on desktop */}
			<div className="grid w-full grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 lg:grid-cols-4">
				{quotes.map((quote) => (
					<InsuranceQuoteCard
						key={quote.id}
						id={quote.id}
						company={quote.company}
						companyLogo={quote.companyLogo}
						price={quote.price}
						recommended={quote.recommended}
						compareChecked={compareList.includes(quote.id)}
						onCompareChange={() => handleToggleCompare(quote.id)}
						onSelectClick={() => handleSelectQuote(quote.id)}
						selected={selectedQuote === quote.id}
					/>
				))}
			</div>

			{/* Footer buttons - Desktop only */}
			<div className="hidden w-full flex-col-reverse items-center justify-between gap-3 md:flex md:flex-row md:gap-0">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="w-full overflow-hidden rounded-xl px-6 py-4 md:w-auto"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="primary"
					appearance="solid"
					size="l"
					onClick={handleCompare}
					disabled={compareList.length === 0}
					className="w-full overflow-hidden rounded-xl px-6 py-4 md:w-auto"
				>
					Comparar
				</Button>
			</div>

			{/* Mobile footer - Fixed at bottom */}
			<div className="fixed right-0 bottom-0 left-0 z-10 border-t border-neutral-400 bg-white px-6 py-3 md:hidden">
				<div className="flex items-center justify-between">
					<Button
						variant="primary"
						appearance="ghost"
						size="m"
						onClick={onBack}
						className="gap-2 rounded-xl px-4 py-3"
					>
						<Icon name="arrow-left" className="size-4" />
						Atrás
					</Button>
					<Button
						variant="primary"
						appearance="solid"
						size="m"
						onClick={handleCompare}
						disabled={compareList.length === 0}
						className="gap-2 rounded-xl px-4 py-3"
					>
						Comparar
					</Button>
				</div>
			</div>
		</div>
	)
}
